import { FC, useState } from 'react';

import { ApolloQueryResult } from '@apollo/client';
import { Send } from '@mui/icons-material';
import { Button } from '@mui/material';
import { Formik } from 'formik';
import { GetSalesInvoice } from 'types/graphql';

import { useParams } from '@redwoodjs/router';
import { useMutation } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { FieldInput } from 'src/components/FormikComponents';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { showSuccessToast } from 'src/components/Toasts/toast';
import { RESEND_SALES_INVOICE } from 'src/graphql/mutations/resendSalesInvoice.mutation';
import { onError } from 'src/utils/utils';
import { ResendFormToBillingContactValidation } from 'src/validation/SalesInvoice';

interface ResendSalesInvoice {
    billingContactEmailAddress: string;
    refetchSalesInvoice: (
        variables?: Partial<GraphQLOperationVariables>
    ) => Promise<ApolloQueryResult<GetSalesInvoice>>;
    isSalesInvoiceEmailFailed: boolean;
}

export const ResendSalesInvoice: FC<ResendSalesInvoice> = ({
    billingContactEmailAddress,
    refetchSalesInvoice,
    isSalesInvoiceEmailFailed,
}) => {
    const { id } = useParams();
    const [showModal, setShowModal] = useState(false);

    const [resendSalesInvoice] = useMutation(RESEND_SALES_INVOICE, {
        onError,
        onCompleted: (data) => {
            const invoiceId = data?.resendSalesInvoice?.id;

            if (isSalesInvoiceEmailFailed) {
                refetchSalesInvoice();
            }

            if (invoiceId) {
                showSuccessToast('Sales invoice resent!');
            }
        },
    });

    const onSubmit = async (sender, billingContactEmailAddress, coverMessage) => {
        await resendSalesInvoice({
            variables: {
                input: {
                    sender,
                    billingContactEmailAddress,
                    coverMessage,
                    id,
                },
            },
        });
    };

    return (
        <>
            <Flex
                justifyContent="center"
                alignItems="center"
                color="white"
                bgcolor="#F39E4E"
                borderRadius="4px"
                padding="8px"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    return setShowModal(true);
                }}
                data-testid="resend-sales-invoice-button"
            >
                <Send height="24px" width="24px" />
            </Flex>
            {showModal && (
                <ModalBox
                    handleCloseModal={() => {
                        return setShowModal(false);
                    }}
                    open={showModal}
                    title="Resend Sales Invoice"
                >
                    <Formik
                        initialValues={{
                            sender: process.env.APP_EMAIL_SENDER,
                            billingContactEmailAddress,
                            coverMessage: '',
                        }}
                        onSubmit={({ sender, billingContactEmailAddress, coverMessage }) => {
                            onSubmit(sender, billingContactEmailAddress, coverMessage);
                        }}
                        validationSchema={ResendFormToBillingContactValidation}
                    >
                        {({ handleSubmit }) => {
                            return (
                                <Flex gap="16px" flexDirection="column" padding="16px 8px">
                                    <FieldInput
                                        name="billingContactEmailAddress"
                                        label="Billing Contact Email"
                                        placeholder="Billing Contact Email"
                                    />
                                    <FieldInput name="sender" label="Sender" disabled />
                                    <FieldInput
                                        name="coverMessage"
                                        label="Cover Message"
                                        placeholder="Cover Message"
                                    />
                                    <Button
                                        onClick={() => {
                                            return handleSubmit();
                                        }}
                                        variant="main"
                                    >
                                        Resend
                                    </Button>
                                </Flex>
                            );
                        }}
                    </Formik>
                </ModalBox>
            )}
        </>
    );
};
